import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../explore-wheel.css";

export type WheelPocket = {
  number: number;
  colour: "red" | "black" | "green";
  label: string;
  title: string;
  text: string;
  to: string;
  linkLabel: string;
};

// Pocket numbers follow a real wheel from 0. Each pocket is a part of the store: nothing is staked or won.
export const POCKETS: WheelPocket[] = [
  { number: 0, colour: "green", label: "Build", title: "Build your machine", text: "Choose the game, then the bill acceptor, printer and cabinet. Priced by quotation.", to: "/build-your-machine", linkLabel: "Build your machine" },
  { number: 32, colour: "red", label: "Kiosk", title: "Cash Redemption Kiosk", text: "Self-service ticket redemption for your venue, so staff spend less time on payouts.", to: "/product/6abd00000000000000000001", linkLabel: "See the kiosk" },
  { number: 15, colour: "black", label: "Machines", title: "Gaming machines", text: "Buy one machine, or a linked setup for your venue.", to: "/gaming-machines", linkLabel: "Browse machines" },
  { number: 19, colour: "red", label: "Platinum", title: "Platinum Bill Acceptor", text: "UV note validation, anti-stringing protection and USB updates.", to: "/product/6abc8ea835978d0228ff0c71", linkLabel: "See the Platinum" },
  { number: 4, colour: "black", label: "Leasing", title: "Leasing", text: "Lease gaming machines instead of buying outright.", to: "/leasing", linkLabel: "Explore leasing" },
  { number: 21, colour: "red", label: "Party Link", title: "Roulette Scratcher Party Link", text: "Roulette and scratch-off games sharing one credit balance.", to: "/product/6abff20a6df5c229bb8c36df", linkLabel: "See the linking system" },
  { number: 2, colour: "black", label: "Service", title: "Repairs and service", text: "Faults, maintenance, installation and setup by our team.", to: "/services", linkLabel: "See our services" },
  { number: 25, colour: "red", label: "Parts", title: "Parts and accessories", text: "Bill acceptors, printers, power supplies, cables and more.", to: "/products", linkLabel: "Shop parts" },
];

const SEGMENT = 360 / POCKETS.length;
const SPIN_MS = 4700;
const FILL = { red: "#b71827", black: "#17171c", green: "#0f7a4a" };
const C = 200;
const R = 190;

function point(angle: number, radius: number) {
  const a = (angle * Math.PI) / 180;
  return [C + radius * Math.sin(a), C - radius * Math.cos(a)];
}

function reducedMotion() {
  return !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

// Homepage "not sure where to start?" wheel. The result is decided before the wheel turns,
// and the wheel is stopped with that pocket under the pointer.
export default function ExploreWheel({ random = Math.random }: { random?: () => number }) {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<WheelPocket | null>(null);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);

  function spin() {
    if (spinning) return;
    const index = Math.min(POCKETS.length - 1, Math.floor(random() * POCKETS.length));
    const still = reducedMotion();
    const centre = index * SEGMENT + SEGMENT / 2;
    const jitter = (random() - 0.5) * SEGMENT * 0.6;
    const current = ((rotation % 360) + 360) % 360;
    const target = (((360 - centre + jitter) % 360) + 360) % 360;
    setRotation(rotation + (still ? 0 : 360 * 5) + ((target - current + 360) % 360));
    setSpinning(true);
    setResult(null);
    timer.current = window.setTimeout(() => {
      setResult(POCKETS[index]);
      setSpinning(false);
    }, still ? 0 : SPIN_MS);
  }

  return (
    <section className="hs-wrap ew-section" aria-labelledby="home-wheel-title">
      <div className="ew-panel">
        <div className="ew-wheel-box">
          <svg className="ew-pointer" viewBox="0 0 34 42" aria-hidden="true">
            <path d="M17 42 L2 8 A16 16 0 0 1 32 8 Z" fill="#ffd25a" stroke="#17171c" strokeWidth="2" />
            <circle cx="17" cy="14" r="5" fill="#17171c" />
          </svg>
          <svg
            className="ew-wheel"
            viewBox="0 0 400 400"
            style={{ transform: `rotate(${rotation}deg)` }}
            role="img"
            aria-label={`Roulette wheel with ${POCKETS.length} parts of the store`}
          >
            <circle cx={C} cy={C} r={R + 8} fill="#c99a1e" />
            <circle cx={C} cy={C} r={R + 3} fill="#17171c" />
            {POCKETS.map((pocket, i) => {
              const [x0, y0] = point(i * SEGMENT, R);
              const [x1, y1] = point((i + 1) * SEGMENT, R);
              return (
                <g key={pocket.number}>
                  <path d={`M${C} ${C} L${x0} ${y0} A${R} ${R} 0 0 1 ${x1} ${y1} Z`} fill={FILL[pocket.colour]} stroke="#ffd25a" strokeWidth="2" />
                  <g transform={`rotate(${i * SEGMENT + SEGMENT / 2} ${C} ${C})`}>
                    <text x={C} y={C - R + 36} textAnchor="middle" fill="#ffd25a" fontWeight="800" fontSize="22">{pocket.number}</text>
                    <text x={C} y={C - R + 72} textAnchor="middle" fill="#fff" fontWeight="700" fontSize="14">{pocket.label}</text>
                  </g>
                </g>
              );
            })}
            <circle cx={C} cy={C} r="58" fill="#17171c" stroke="#ffd25a" strokeWidth="4" />
            <circle cx={C} cy={C} r="40" fill="#b71827" />
            <text x={C} y={C + 11} textAnchor="middle" fill="#ffd25a" fontWeight="800" fontSize="32">T</text>
          </svg>
        </div>
        <div className="ew-side">
          <p className="ew-eyebrow">NOT SURE WHERE TO START?</p>
          <h2 id="home-wheel-title">Spin to explore the store.</h2>
          <p>
            Each pocket is a part of the shop: machines, parts, the kiosk, leasing or service. There’s nothing to win. It just
            gives you somewhere to start.
          </p>
          <button type="button" className="ew-spin" onClick={spin} disabled={spinning}>
            {spinning ? "Spinning…" : result ? "Spin again" : "Spin the wheel"}
          </button>
          <div className="ew-result" aria-live="polite">
            {result ? (
              <>
                <span className={`ew-pocket ew-${result.colour}`} aria-hidden="true">{result.number}</span>
                <div>
                  <h3>{result.title}</h3>
                  <p>{result.text}</p>
                  <Link to={result.to}>{result.linkLabel} →</Link>
                </div>
              </>
            ) : (
              <p>{spinning ? "Where will it stop?" : "Press spin and the wheel picks one of eight places to start."}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
